/**
 * `/quote/drafts` — Operator approval queue (M-QUOTE.3.c).
 */
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useApproveQuoteDraft,
  useQuoteDraftsPending,
  useRejectQuoteDraft,
} from "@/hooks/use-quote";
import { formatTimestamp } from "@/lib/formatters";

export function QuoteDraftsPage() {
  const pending = useQuoteDraftsPending();
  const approveMut = useApproveQuoteDraft();
  const rejectMut = useRejectQuoteDraft();
  const [reason, setReason] = useState<Record<string, string>>({});

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Quote</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Drafts queue</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Review and approve AI-drafted quote replies before they go out to customers.
        </p>
      </header>

      {pending.isLoading && <Skeleton className="h-24 w-full" />}
      {pending.error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">
              Couldn't load drafts: {pending.error.message}
            </p>
          </CardContent>
        </Card>
      )}
      {pending.data && pending.data.items.length === 0 && !pending.isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>Inbox zero ✨</CardTitle>
            <CardDescription>
              No drafts waiting on you. New drafts land here when a follow-up sequence step fires or
              when an AI workflow produces a quote response.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      {pending.data && pending.data.items.length > 0 && (
        <ul className="grid grid-cols-1 gap-3">
          {pending.data.items.map((d) => (
            <li key={d.id}>
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-base">{d.subject}</CardTitle>
                    <Badge variant="outline">{d.channel}</Badge>
                  </div>
                  <CardDescription>
                    To {d.to_email} · from {d.from_email} · {formatTimestamp(d.created_at)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  <pre className="whitespace-pre-wrap rounded bg-muted p-3 text-sm">{d.body}</pre>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      onClick={() => approveMut.mutate(d.id)}
                      disabled={approveMut.isPending || rejectMut.isPending}
                    >
                      Approve & send
                    </Button>
                    <input
                      className="rounded border border-input bg-background px-3 py-2 text-sm flex-1 min-w-[200px]"
                      placeholder="Rejection reason (optional)"
                      value={reason[d.id] ?? ""}
                      onChange={(e) => setReason((r) => ({ ...r, [d.id]: e.target.value }))}
                    />
                    <Button
                      variant="outline"
                      onClick={() =>
                        rejectMut.mutate({
                          id: d.id,
                          body: reason[d.id]?.trim()
                            ? { rejection_reason: reason[d.id]?.trim() }
                            : {},
                        })
                      }
                      disabled={approveMut.isPending || rejectMut.isPending}
                    >
                      Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
